import { FC, ReactNode } from "react";
import usePermissions, { PermissionAction } from "../../hooks/use-permissions";

export interface PermissionGateProps {
  module: string | string[];
  action?: PermissionAction;
  fallback?: ReactNode;
  children: ReactNode | ((permissionsState: ReturnType<typeof usePermissions>) => ReactNode);
}

/**
 * Declarative component to conditionally render UI elements based on user permissions.
 * 
 * Example:
 * <PermissionGate module={permissions.DASHBOARD} action="can_export">
 *    <ExportButton onClick={handleExport} />
 * </PermissionGate>
 */
export const PermissionGate: FC<PermissionGateProps> = ({
  module,
  action,
  fallback = null,
  children,
}) => {
  const permState = usePermissions(module);
  const { hasPermission } = permState;

  const isAllowed = hasPermission(module, action);

  if (!isAllowed) {
    return <>{fallback}</>;
  }

  if (typeof children === "function") {
    return <>{children(permState)}</>;
  }

  return <>{children}</>;
};

export default PermissionGate;
